/**
 * UC-A-4 (UI-04, Final PRD §19–21, §24–25, §29) — four tracks on one time axis, built only from data the sheet already
 * loads: segments, the shared delivery group read-model (planned / actual ratio slices, worker assignments, members) and
 * the shift's own workers. Pure (Jest-tested). Minutes are relative to the shift's own midnight; "+1" past it.
 */
import { shiftSpan, toMinutes, plusOne } from './shiftSheetRules';

const dayOrdinal = (iso) => Math.floor(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86400000);

/** Group `*_abs` minutes → minutes since the shift's midnight (the group window may start on an earlier/later day). */
export const groupOffset = (group, shift) => {
  const w = group?.window;
  if (!w?.date || !shift?.date || w.start_abs == null) return null;
  const groupMidnight = w.start_abs - (toMinutes(w.start_time) ?? 0);
  return groupMidnight + (dayOrdinal(shift.date) - dayOrdinal(w.date)) * 1440;
};

const relSeg = (shift, row) => {
  const { start: S } = shiftSpan(shift);
  let s = toMinutes(row.allocation_start_time) ?? S;
  let e = toMinutes(row.allocation_end_time) ?? s;
  if (s < S) s += 1440;
  if (e <= s) e += 1440;
  return [s, e];
};

const gapsIn = (start, end, blocks) => {
  const out = [];
  let cursor = start;
  [...blocks].sort((a, b) => a.start - b.start).forEach((b) => {
    if (b.start > cursor) out.push({ start: cursor, end: b.start, kind: 'gap', label: 'Unallocated' });
    cursor = Math.max(cursor, b.end);
  });
  if (cursor < end) out.push({ start: cursor, end, kind: 'gap', label: 'Unallocated' });
  return out;
};

const RATIO_TONE = (ratio) => (ratio === 'OPEN' || ratio === 'UNCOVERED' ? 'danger' : /^[2-9]:1$/.test(ratio) ? 'accent' : 'neutral');

export function buildTimelineTracks({ shift, segments = [], group = null, assignedStaff = [], conflicts = [], serviceLabel } = {}) {
  const { start: S, end: E } = shiftSpan(shift);
  const off = groupOffset(group, shift);
  const rel = (abs) => (off == null ? null : abs - off);
  let axisStart = S; let axisEnd = E;

  // Services
  const segBlocks = (segments || []).filter((r) => r.status !== 'DELETED').map((r) => {
    const [s, e] = relSeg(shift, r);
    axisStart = Math.min(axisStart, s); axisEnd = Math.max(axisEnd, e);
    return { start: s, end: e, kind: 'segment', label: (serviceLabel ? serviceLabel(r) : r.support_item_number || 'Service'), sub: r.operational_ratio || '', id: r.id };
  });
  const services = { key: 'services', label: 'Services', lanes: [{ label: shift?.participant_name || 'Participant', blocks: [...segBlocks, ...gapsIn(S, E, segBlocks)] }] };

  // Coverage — one lane per worker; OPEN hatched
  const coverageLanes = [];
  if (group && off != null) {
    const live = (group.worker_assignments || []).filter((a) => a.status === 'ASSIGNED' || a.status === 'OPEN');
    const byWorker = new Map();
    live.forEach((a) => {
      const key = a.status === 'OPEN' ? 'OPEN' : String(a.worker_id);
      const lane = byWorker.get(key) || { label: a.status === 'OPEN' ? 'Open coverage' : (a.worker_name || `Worker #${String(a.worker_id).slice(-4)}`), blocks: [], open: a.status === 'OPEN' };
      const s = rel(a.start_abs); const e = rel(a.end_abs);
      axisStart = Math.min(axisStart, s); axisEnd = Math.max(axisEnd, e);
      lane.blocks.push({ start: s, end: e, kind: a.status === 'OPEN' ? 'open' : 'worker', label: a.role && a.role !== 'PRIMARY' ? a.role : '', id: a.assignment_id });
      byWorker.set(key, lane);
    });
    (group.ratio_slices || []).filter((sl) => sl.open_coverage || sl.ratio === 'OPEN').forEach((sl) => {
      const lane = byWorker.get('OPEN') || { label: 'Open coverage', blocks: [], open: true };
      lane.blocks.push({ start: rel(sl.start_abs), end: rel(sl.end_abs), kind: 'open', label: '' });
      byWorker.set('OPEN', lane);
    });
    byWorker.forEach((lane) => coverageLanes.push(lane));
  } else if ((assignedStaff || []).length) {
    assignedStaff.forEach((w) => coverageLanes.push({ label: w.name || w.full_name || `Worker #${String(w.id).slice(-4)}`, blocks: [{ start: S, end: E, kind: 'worker', label: '' }] }));
  } else {
    coverageLanes.push({ label: 'Open coverage', open: true, blocks: [{ start: S, end: E, kind: 'open', label: '' }] });
  }
  const coverage = { key: 'coverage', label: 'Coverage', lanes: coverageLanes };

  // Ratio — planned slices (S3); actual slices (S4) when present, read-only
  const ratioLanes = [];
  const sliceBlocks = (slices) => (slices || []).map((sl) => ({ start: rel(sl.start_abs), end: rel(sl.end_abs), kind: 'ratio', tone: RATIO_TONE(sl.ratio), label: sl.ratio === 'OPEN' ? 'Open' : sl.ratio === 'UNCOVERED' ? 'Uncovered' : sl.ratio }));
  if (group && off != null) {
    ratioLanes.push({ label: 'Planned', blocks: sliceBlocks(group.ratio_slices) });
    if ((group.actual_ratio_slices || []).length) ratioLanes.push({ label: 'Actual', blocks: sliceBlocks(group.actual_ratio_slices) });
  } else {
    ratioLanes.push({ label: 'Planned', blocks: segBlocks.filter((b) => b.sub).map((b) => ({ start: b.start, end: b.end, kind: 'ratio', tone: RATIO_TONE(b.sub), label: b.sub })) });
  }
  const ratio = { key: 'ratio', label: 'Ratio', lanes: ratioLanes };

  // Shared — other members (intentional) + accidental conflicts (red with reason)
  const sharedLanes = [];
  if (group && off != null) {
    (group.members || []).filter((m) => String(m.id) !== String(shift?.id)).forEach((m) => {
      const { start, end } = shiftSpan(m);
      const shiftDays = dayOrdinal(m.date) - dayOrdinal(shift.date);
      const s = start + shiftDays * 1440; const e = end + shiftDays * 1440;
      axisStart = Math.min(axisStart, s); axisEnd = Math.max(axisEnd, e);
      sharedLanes.push({ label: m.participant_name || `Participant #${String(m.participant_id).slice(-4)}`, blocks: [{ start: s, end: e, kind: 'shared', intentional: true, label: 'Shared delivery' }] });
    });
  }
  (conflicts || []).forEach((c) => {
    sharedLanes.push({ label: c.worker_name, blocks: (c.booked || []).map((b) => {
      let s = toMinutes(b.start_time) ?? S; let e = toMinutes(b.end_time) ?? s;
      if (e <= s) e += 1440;
      return { start: s, end: e, kind: 'conflict', intentional: false, label: 'Conflict', reason: c.reason };
    }) });
  });
  const shared = { key: 'shared', label: 'Shared', lanes: sharedLanes };

  const ticks = [];
  for (let t = Math.floor(axisStart / 60) * 60; t <= axisEnd; t += 60) ticks.push({ at: t, label: plusOne(t) });
  return { axis: { start: axisStart, end: axisEnd, ticks }, tracks: [services, coverage, ratio, shared] };
}

export const blockLabel = (b) => `${plusOne(b.start)}–${plusOne(b.end)}${b.label ? ` ${b.label}` : ''}`;
